import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  usePmoFlowSummary,
  usePmoScaleLeadTime,
  usePmoSupplyRate,
} from '../../../hooks/usePmoFlow.js';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import { ErrorState } from '../../feedback/ErrorState/index.js';
import { SupplyRateCard } from './SupplyRateCard.js';
import { PhaseCompositionCard } from './PhaseCompositionCard.js';
import { PhaseTrendCard } from './PhaseTrendCard.js';
import { ScaleLeadTimeCard } from './ScaleLeadTimeCard.js';
import type { PmoScaleExcludeMode } from '../../../types/pmo-flow.js';

/** 集計期間の選択肢（月数）。 @graph-connects none */
const MONTHS_OPTIONS = [12, 6, 3] as const;

/**
 * PMO フェーズリードタイム画面のコンテナ。集計期間（月数）を持ち、
 * サマリーを 1 回取得して構成カード / 推移カードへ配る。
 *
 * この画面は「測れているフェーズの数値」と「測れていないフェーズの存在」を
 * 同じ画面に並べることを目的にしているため、未計測区間を隠さない。
 *
 * 最後の規模別リードタイムカードは、工程を割らずに「その規模の案件は結局どれだけ
 * 掛かるか」を見る別口径の指標なので、工程の内訳（構成 / 推移）を見終わった後に置く。
 *
 * 充足率カードだけは集計期間セレクトに連動しない（分母は先 2 週間分の予算で全社横断・
 * 期間非依存）ため、期間を変えても再取得されない位置＝最上部に置く。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMOフェーズリードタイム画面のコンテナ。集計期間セレクトを持ち、充足率カード・フェーズ構成カード・月次推移カード・案件規模別リードタイムカードを順に表示する
 * @graph-connects usePmoFlowSummary [calls] フェーズリードタイムサマリー取得フック呼び出し
 * @graph-connects usePmoSupplyRate [calls] 充足率取得フック呼び出し
 * @graph-connects usePmoScaleLeadTime [calls] 案件規模別リードタイム取得フック呼び出し
 * @graph-connects SupplyRateCard [calls] 充足率カード表示
 * @graph-connects PhaseCompositionCard [calls] フェーズ構成カード表示
 * @graph-connects PhaseTrendCard [calls] 月次コホート推移カード表示
 * @graph-connects ScaleLeadTimeCard [calls] 案件規模別リードタイムカード表示
 * @graph-connects ErrorState [calls] エラー表示描画
 * @graph-connects Select [calls] 集計期間セレクト描画
 */
export function PmoFlowView() {
  const { t } = useTranslation();
  const [months, setMonths] = useState<number>(12);
  // 規模別リードタイムはリリース完了月単位で、上部の期間セレクトとは別軸。
  // 初期値は null にしてサーバー既定（最新月）に従う
  const [scaleMonth, setScaleMonth] = useState<string | null>(null);
  // 遅い側の除外は既定 none（まず実データの姿を出し、外した姿は切り替えて見る）
  const [scaleExclude, setScaleExclude] = useState<PmoScaleExcludeMode>('none');

  const { data, isLoading, error, refetch } = usePmoFlowSummary({ months });
  // 充足率は分母が先 2 週間分の予算で期間セレクトに連動しないため、months を渡さない
  const supplyRate = usePmoSupplyRate();
  // 規模別リードタイムも集計期間セレクトに連動しない（母数が期間に直結するため直近半年で固定）
  const scaleLeadTime = usePmoScaleLeadTime(scaleMonth, scaleExclude);

  const monthsOptions = MONTHS_OPTIONS.map((value) => ({
    value: String(value),
    // 12ヶ月は既存の期間ラベル（過去1年間）を再利用する
    label: value === 12 ? t('time.last1Year') : t('kpi.pmoFlow.monthsOption', { months: value }),
  }));

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-bold text-[var(--color-text-primary)]">
            {t('kpi.pmoFlow.title')}
          </h2>
          <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
            {t('kpi.pmoFlow.description')}
          </p>
        </div>
        <Select
          options={monthsOptions}
          value={String(months)}
          onChange={(e) => {
            setMonths(Number(e.target.value));
          }}
          size="sm"
          aria-label={t('kpi.pmoFlow.monthsLabel')}
        />
      </div>

      {error && (
        <ErrorState
          title={t('feedback.errorTitle')}
          message={t('feedback.errorMessage')}
          onRetry={() => {
            void refetch();
          }}
        />
      )}

      <SupplyRateCard data={supplyRate.data} isLoading={supplyRate.isLoading} />
      <ScaleLeadTimeCard
        data={scaleLeadTime.data}
        isLoading={scaleLeadTime.isLoading}
        selectedMonth={scaleMonth}
        onSelectMonth={setScaleMonth}
        excludeMode={scaleExclude}
        onSelectExcludeMode={setScaleExclude}
      />
      <PhaseCompositionCard data={data} isLoading={isLoading} />
      <PhaseTrendCard segments={data?.segments ?? []} months={months} />
    </div>
  );
}
